import * as Sentry from "@sentry/react"
import { VITE_CHAIN_ID, VITE_DEPLOY_ENVIRONMENT, VITE_SENTRY_DEBUG, VITE_SENTRY_DSN } from "./env"
import { getErrorCode, getErrorMessage } from "./errors"

export function initSentry() {
	if (!VITE_SENTRY_DSN) {
		return
	}

	Sentry.init({
		dsn: VITE_SENTRY_DSN,
		environment: VITE_DEPLOY_ENVIRONMENT || "development",

		// Filter out noisy errors
		ignoreErrors: [
			// User-initiated actions
			"User denied",
			"user rejected",
			"User rejected",
			// Network errors that are expected
			"Failed to fetch",
			"NetworkError",
			"Load failed",
			// Browser extensions
			"ResizeObserver loop",
			// Wallet errors that are expected
			"Connector not found",
			"Unable to find source wallet connector", // Privy Solana bridge when wallet not connected
		],

		// Before sending, add extra context
		beforeSend(event) {
			// Don't send in development unless explicitly enabled
			if (VITE_DEPLOY_ENVIRONMENT === "development" && !VITE_SENTRY_DEBUG) {
				return null
			}

			// Add chain context
			event.tags = {
				...event.tags,
				chainId: VITE_CHAIN_ID || "unknown",
			}

			return event
		},

		// Send default PII (IP, user agent) for debugging
		sendDefaultPii: true,
	})
}

interface SentryUserInput {
	id: string
	email?: string | null
	walletAddress?: string | null
	displayName?: string | null
}

/**
 * Set the current user context for Sentry.
 * Call this after authentication to associate errors with users.
 */
export function setSentryUser(user: SentryUserInput) {
	if (!VITE_SENTRY_DSN) {
		return
	}

	// Sentry.setUser accepts additional custom properties beyond the typed interface
	const sentryUser: Sentry.User & { walletAddress?: string } = {
		id: user.id,
		email: user.email || undefined,
		username: user.displayName || undefined,
		walletAddress: user.walletAddress || undefined,
	}
	Sentry.setUser(sentryUser)
}

export function clearSentryUser() {
	if (!VITE_SENTRY_DSN) {
		return
	}
	Sentry.setUser(null)
}

export function captureTradeError(
	error: Error,
	context: {
		action: "buy" | "sell" | "claim"
		marketAddress?: string
		bucket?: number
		shares?: string
		usdAmount?: string
		walletAddress?: string
	},
) {
	if (!VITE_SENTRY_DSN) {
		return
	}

	Sentry.withScope(scope => {
		scope.setTag("action", context.action)
		scope.setTag("domain", "trading")

		if (context.marketAddress) {
			scope.setContext("market", {
				address: context.marketAddress,
				bucket: context.bucket,
				shares: context.shares,
				usdAmount: context.usdAmount,
			})
		}

		if (context.walletAddress) {
			scope.setTag("walletAddress", context.walletAddress)
		}

		Sentry.captureException(error)
	})
}

interface ParseTradeErrorMessageParams {
	error: unknown
	action: "buy" | "sell" | "claim"
}

/** Parse a trade error into a user-friendly message */
export function parseTradeErrorMessage({ error, action }: ParseTradeErrorMessageParams): string {
	// First, try to extract structured error code from Convex errors
	const errorCode = getErrorCode(error)

	// Handle known error codes with user-friendly messages
	if (errorCode) {
		switch (errorCode) {
			case "GASLESS_UNAVAILABLE":
				return "Gasless trading unavailable. You'll pay gas for this transaction."
			case "PREPARATION_EXPIRED":
				return "Transaction expired. Please try again."
			case "UNKNOWN_MARKET":
				return "Market not found"
			case "RATE_LIMITED":
				return "Too many requests. Please wait a moment."
			case "UNAUTHENTICATED":
				return "Please sign in to continue"
			case "FORBIDDEN":
			case "UNAUTHORIZED":
				return "Transaction not authorized"
			default:
				// For other codes, use the message from the error
				return getErrorMessage(error, "Transaction failed")
		}
	}

	if (!(error instanceof Error)) {
		return "Transaction failed"
	}

	// Handle custom error classes from useTrade hooks
	// These provide detailed balance information for better UX
	if (error.name === "InsufficientFundsError") {
		// Check for custom properties using property access and type narrowing
		if (
			"required" in error &&
			"available" in error &&
			typeof error.required === "number" &&
			typeof error.available === "number"
		) {
			return `Insufficient USDC: need $${error.required.toFixed(2)}, have $${error.available.toFixed(2)}`
		}
		return "Insufficient USDC balance"
	}

	if (error.name === "InsufficientSharesError") {
		if (
			"required" in error &&
			"available" in error &&
			typeof error.required === "number" &&
			typeof error.available === "number"
		) {
			return `Insufficient shares: trying to sell ${error.required}, have ${error.available}`
		}
		return "Insufficient shares to sell"
	}

	const message = error.message.toLowerCase()

	// Handle contract revert errors with known error names
	// These come from the SDK as "Contract reverted: ErrorName"
	const contractErrorMatch = error.message.match(/Contract reverted: (\w+)/)
	if (contractErrorMatch) {
		const errorName = contractErrorMatch[1]
		const contractErrorMessage = getContractErrorMessage(errorName)
		if (contractErrorMessage) {
			return contractErrorMessage
		}
	}

	// User cancelled the signature request
	if (
		message.includes("user rejected") ||
		message.includes("user denied") ||
		message.includes("rejected the request")
	) {
		return "Transaction cancelled"
	}

	// Insufficient USDC balance for buy
	if (
		action === "buy" &&
		(message.includes("insufficient") || message.includes("exceeds balance"))
	) {
		return "Insufficient USDC balance"
	}

	// Insufficient shares for sell
	if (
		action === "sell" &&
		(message.includes("insufficient") || message.includes("exceeds balance"))
	) {
		return "Insufficient shares to sell"
	}

	// Nothing to claim
	if (
		action === "claim" &&
		(message.includes("nothing to claim") || message.includes("no winnings"))
	) {
		return "No winnings to claim"
	}

	// Insufficient ETH for gas (fallback to user-pays-gas)
	if (message.includes("insufficient funds") || message.includes("insufficient eth")) {
		return "Insufficient ETH for gas. Please add ETH to your wallet."
	}

	// Market is closed or not open
	if (
		message.includes("market closed") ||
		message.includes("cutoff") ||
		message.includes("not open")
	) {
		return "Market is closed for trading"
	}

	// Slippage too high
	if (
		message.includes("slippage") ||
		message.includes("max cost") ||
		message.includes("min proceeds")
	) {
		return "Price changed too much. Please try again."
	}

	// Deadline expired
	if (message.includes("deadline") || message.includes("expired")) {
		return "Transaction expired. Please try again."
	}

	// Gasless relayer errors (from ConvexError)
	if (message.includes("gasless_unavailable") || message.includes("gasless unavailable")) {
		return "Gasless trading unavailable. You'll pay gas for this transaction."
	}

	// Unauthorized bundle (should not happen in normal flow)
	if (message.includes("unauthorized") || message.includes("security")) {
		return "Transaction not authorized"
	}

	// Backend relayer misconfigured (using RPC that doesn't support sendTransaction)
	if (
		message.includes("unsupported method") ||
		message.includes("eth_sendtransaction") ||
		message.includes("method is not supported") ||
		message.includes("-32604") ||
		message.includes("-32600")
	) {
		return "Trading temporarily unavailable. Please try again later."
	}

	// Network/RPC errors
	if (message.includes("network") || message.includes("rpc") || message.includes("timeout")) {
		return "Network error. Please check your connection and try again."
	}

	// Generic transaction reverted
	if (message.includes("reverted") || message.includes("execution reverted")) {
		return "Transaction failed. Please try again."
	}

	// If we don't recognize the error, show a truncated version
	const cleanMessage = error.message.replace(/\s+/g, " ").trim()
	return cleanMessage.length > 60 ? `${cleanMessage.slice(0, 60)}...` : cleanMessage
}

// =============================================================================
// Contract Error Mapping
// =============================================================================

/**
 * Map contract error names to user-friendly messages.
 * These errors come from BreakoutMarket.sol via the SDK.
 */
const CONTRACT_ERROR_MESSAGES: Record<string, string> = {
	// Solvency errors
	InsufficientReserve:
		"This trade is too large for the current market liquidity. Try a smaller amount.",
	InsufficientLiquidity: "Not enough liquidity in the market. Try a smaller amount.",

	// Trading errors
	SlippageExceeded: "Price moved too much during the trade. Try again or increase slippage.",
	ZeroShares: "Trade amount too small to receive any shares.",
	ZeroAmount: "Amount must be greater than zero.",
	InsufficientShares: "You don't have enough shares to sell this amount.",

	// Market state errors
	MarketNotOpen: "This market is not yet open for trading.",
	MarketNotClosed: "This market hasn't closed yet.",
	MarketNotResolved: "This market hasn't been resolved yet.",
	MarketNotFinalized: "This market hasn't been finalized yet. Please wait.",
	MarketAlreadyFinalized: "This market has already been finalized.",
	MarketPaused: "This market is temporarily paused.",

	// Timing errors
	DeadlineExpired: "Transaction expired. Please try again.",

	// Claim errors
	AlreadyClaimed: "You've already claimed your winnings from this market.",
	NothingToClaim: "No winnings to claim from this market.",

	// Validation errors
	InvalidBucket: "Invalid outcome selected.",
	InvalidWinningOutcome: "Invalid winning outcome.",
	InvalidSignature: "Invalid signature. Please try again.",
	Unauthorized: "You're not authorized to perform this action.",

	// Permit errors
	InvalidPermitToken: "Invalid token for permit.",
	InvalidTransferRecipient: "Invalid transfer recipient.",
	InsufficientTransferAmount: "Transfer amount is insufficient.",
}

/**
 * Get a user-friendly message for a contract error name.
 * Returns undefined if the error name is not recognized.
 */
function getContractErrorMessage(errorName: string | undefined): string | undefined {
	if (!errorName) {
		return undefined
	}
	return CONTRACT_ERROR_MESSAGES[errorName]
}

// Re-export Sentry for direct usage where needed
export { Sentry }
